import React, { HTMLProps, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AccountType, PensionContext, recurringPaymentInterval } from '../../../../contexts/pension-robot/PensionContext';
import { toPrice } from '../../../../utils/parsers';
import { OtherIncome } from '../../../../contexts/pension-robot/PensionContext';
import { Button } from '../../../general/Button';

export const SummaryDisclosure: React.FC<{
    isGoalAchieved: boolean,
    isGoalAchievedPPD: boolean, 
    difference: number,
    differencePPD: number,
} & HTMLProps<HTMLDivElement>> = ({ isGoalAchieved, isGoalAchievedPPD, difference, differencePPD }) => {
    const {
        parameters: { personalInfo, expensesInfo, supplementInfo }
    } = useContext(PensionContext);
    const { t } = useTranslation('pension');

    const legalPensionAge = 67; // TODO: get legal retirement age from pensionCaldData api when this is added.
    const pensionAgeDelta = !!personalInfo.self?.pensionAge 
        ? personalInfo.self.pensionAge - legalPensionAge
        : 0;
    const pensionAgeDeltaPartner = !!personalInfo.partner?.pensionAge 
        ? personalInfo.partner.pensionAge - legalPensionAge
        : 0;

    const renderYearSpaceAccount = (!!supplementInfo?.yearSpace ? supplementInfo.yearSpace > 0 : false) && !!supplementInfo?.yearSpacePensionAccount;
    const renderAdditionalSavingsAccount = !!supplementInfo?.self?.additionalSavingAccount?.isUsed;
    const renderAdditionalInvestmentAccount = !!supplementInfo?.self?.additionalInvestmentAccount?.isUsed;
    const renderGift = !!supplementInfo?.otherIncome?.gift ? supplementInfo.otherIncome.gift > 0 : false;
    const renderHeritage = !!supplementInfo?.otherIncome?.heritage ? supplementInfo.otherIncome.heritage > 0 : false;
    const renderSurplusHouse = !!supplementInfo?.otherIncome?.surplusHouse ? supplementInfo.otherIncome.surplusHouse > 0 : false;
    const renderOther = !!supplementInfo?.otherIncome?.other ? supplementInfo.otherIncome.other > 0 : false;
    const renderOtherIncome = !!supplementInfo?.otherIncomeAvailable && (renderGift || renderHeritage || renderSurplusHouse || renderOther);

    return (
        <div className="flex flex-col gap-4 p-4 border-b">
            <div>
                {t('pension-result-screen-summary-introduction')}
            </div>
            {!!personalInfo.isCalculatedTogether && t('pension-result-screen-summary-overage-shortage-partner-prefix')}
            <BNDList>
                <OverageShortage isGoalAchieved={isGoalAchieved} difference={difference} />
                {!!personalInfo.isCalculatedTogether && 
                    <OverageShortagePartner isGoalAchievedPPD={isGoalAchievedPPD} differencePPD={differencePPD} />}
            </BNDList>
            <div>
                {t('pension-result-screen-summary-based-on')}
            </div>
            {(pensionAgeDelta !== 0 || (pensionAgeDeltaPartner !== 0 && !!personalInfo.isCalculatedTogether)) && 
                <BNDList>
                    {(pensionAgeDelta !== 0) && 
                        <PensionAge pensionAgeDelta={pensionAgeDelta} legalPensionAge={legalPensionAge} />}
                    {(pensionAgeDeltaPartner !== 0 && !!personalInfo.isCalculatedTogether) && 
                        <PensionAgePartner pensionAgeDeltaPartner={pensionAgeDelta} legalPensionAge={legalPensionAge} />}
                </BNDList>}
            <div>
                <Trans
                    t={t}
                    i18nKey={personalInfo.isCalculatedTogether 
                        ? 'pension-result-screen-summary-net-monthly-partner' 
                        : 'pension-result-screen-summary-net-monthly'}
                    values={{
                        value: toPrice(expensesInfo?.goal ?? 0, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        })
                    }}
                >
                    ...
                    <span className="font-bold">value</span>
                    ...
                </Trans>
            </div>
            {(renderYearSpaceAccount || renderAdditionalInvestmentAccount || renderAdditionalSavingsAccount) &&
                <BNDList>
                    {renderYearSpaceAccount && 
                        <YearspaceAccount 
                            accountType={supplementInfo?.yearSpacePensionAccount?.typeOfAdditionalPensionAccount ?? 'saving'} 
                            amount={supplementInfo?.yearSpacePensionAccount?.monthlyPaymentAmount ?? 0} />}
                    {renderAdditionalSavingsAccount && 
                        <AdditionalSavingAccount 
                            amountStart={supplementInfo?.self?.additionalSavingAccount?.account.currentAmount ?? 0}
                            period={supplementInfo?.self?.additionalSavingAccount?.account.recurringPaymentInterval ?? 'month'} 
                            amountPeriod={supplementInfo?.self?.additionalSavingAccount?.account.recurringPaymentAmount ?? 0} />}
                    {renderAdditionalInvestmentAccount && 
                        <AdditionalInvestmentAccount 
                            amountStart={supplementInfo?.self?.additionalInvestmentAccount?.account.currentAmount ?? 0}
                            period={supplementInfo?.self?.additionalInvestmentAccount?.account.recurringPaymentInterval ?? 'month'} 
                            amountPeriod={supplementInfo?.self?.additionalInvestmentAccount?.account.recurringPaymentAmount ?? 0} />}
                </BNDList>}
            {renderOtherIncome && (
                <div>
                    {t('pension-result-screen-summary-other-income')}
                </div>)}
            {renderOtherIncome &&
                <BNDList>
                    {renderGift && <OtherIncomeText otherIncome={'gift'} amount={supplementInfo?.otherIncome?.gift ?? 0} />}
                    {renderHeritage && <OtherIncomeText otherIncome={'heritage'} amount={supplementInfo?.otherIncome?.heritage ?? 0} />}
                    {renderSurplusHouse && <OtherIncomeText otherIncome={'surplusHouse'} amount={supplementInfo?.otherIncome?.surplusHouse ?? 0} />}
                    {renderOther && <OtherIncomeText otherIncome={'other'} amount={supplementInfo?.otherIncome?.other ?? 0} />}
                </BNDList>}
        </div>
    );
};

const OverageShortage: React.FC<{isGoalAchieved: boolean, difference: number}> = ({ isGoalAchieved, difference }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);

    return (
        <div>
            <Trans
                t={t}
                i18nKey={!!personalInfo.isCalculatedTogether 
                    ? 'pension-result-screen-summary-overage-shortage-partner-1' 
                    : 'pension-result-screen-summary-overage-shortage'}
                values={{
                    overageOrShortage: getOverageOrShortage(isGoalAchieved),
                    value: toPrice(difference, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })
                }}
            >
                ...
                <span className="font-bold">overageOrShortage</span>
                <span className="font-bold">value</span>
                ...
            </Trans>
        </div>
    );
};

const OverageShortagePartner: React.FC<{ isGoalAchievedPPD: boolean, differencePPD: number }> = ({ isGoalAchievedPPD, differencePPD }) => {
    const { t } = useTranslation('pension');

    return (
        <div>
            <Trans
                t={t}
                i18nKey='pension-result-screen-summary-overage-shortage-partner-2'
                values={{
                    overageOrShortage: getOverageOrShortage(isGoalAchievedPPD),
                    value: toPrice(differencePPD, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })
                }}
            >
                ...
                <span className="font-bold">overageOrShortage</span>
                <span className="font-bold">value</span>
                ...
            </Trans>
        </div>
    );
};

const PensionAge: React.FC<{ pensionAgeDelta: number, legalPensionAge: number }> = ({ pensionAgeDelta, legalPensionAge }) => {
    const { t } = useTranslation('pension')
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);

    return (
        <div>
            <Trans
                t={t}
                i18nKey='pension-result-screen-summary-pension-age-altered'
                values={{
                    increaseOrDecrease: getPensionAgeAltered(pensionAgeDelta),
                    age: personalInfo.self?.pensionAge ?? legalPensionAge
                }}
            >
                ...
                <span className="font-bold">increaseOrDecrease</span>
                <span className="font-bold">age</span>
                ...
            </Trans>
        </div>
    );
};

const PensionAgePartner: React.FC<{ pensionAgeDeltaPartner: number, legalPensionAge: number }> = ({ pensionAgeDeltaPartner, legalPensionAge }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);

    return (
        <div>
            <Trans
                t={t}
                i18nKey='pension-result-screen-summary-pension-age-altered-partner'
                values={{
                    increaseOrDecrease: getPensionAgeAltered(pensionAgeDeltaPartner),
                    age: personalInfo.partner?.pensionAge ?? legalPensionAge
                }}
            >
                ...
                <span className="font-bold">increaseOrDecrease</span>
                <span className="font-bold">age</span>
                ...
            </Trans>
        </div>
    );
};

const YearspaceAccount: React.FC<{ 
    accountType: AccountType, 
    amount: number }> 
= ({ accountType, amount }) => {
    const { t } = useTranslation('pension');

    return (
        <div className='flex flex-row flex-wrap sm:flex-nowrap items-center gap-4'>
            <div>
                <Trans
                    t={t}
                    i18nKey={
                        accountType === 'saving' 
                            ? 'pension-result-screen-summary-yearspace-savings'
                            : 'pension-result-screen-summary-yearspace-investment'}
                    values={{
                        valuePeriod: toPrice(amount, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        })
                    }}
                >
                    ...
                    <span className="font-bold">valuePeriod</span>
                    ...
                </Trans>
            </div>
            <div>
                <Button
                    className={`w-48 px-4 font-semibold`}
                    tint="green"
                    size="sm"
                    onClick={() => {} // TODO: implement functionality (ticket 2905, ROBO 419)
                }>
                    {t('general-open-account')}
                </Button>
            </div>
        </div>
    );
};

const AdditionalSavingAccount: React.FC<{ 
    amountStart: number, 
    period: recurringPaymentInterval, 
    amountPeriod: number }> 
= ({ amountStart, period, amountPeriod }) => {
    const { t } = useTranslation('pension');

    return (
        <div className='flex flex-row flex-wrap sm:flex-nowrap items-center gap-4'>
            <div>
                <Trans
                    t={t}
                    i18nKey='pension-result-screen-summary-savings'
                    values={{
                        valueStart: toPrice(amountStart, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                        period: getPeriodText(period),
                        valuePeriod: toPrice(amountPeriod, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        })
                    }}
                >
                    ...
                    <span className="font-bold">valueStart</span>
                    <span className="font-bold">period</span>
                    <span className="font-bold">valuePeriod</span>
                    ...
                </Trans>
            </div>
        </div>
    );
};

const AdditionalInvestmentAccount: React.FC<{
    amountStart: number, 
    period: recurringPaymentInterval, 
    amountPeriod: number }> 
= ({ amountStart, period, amountPeriod }) => {
    const { t } = useTranslation('pension');

    return (
        <div className='flex flex-row flex-wrap sm:flex-nowrap items-center gap-4'>
            <div>
                <Trans
                    t={t}
                    i18nKey='pension-result-screen-summary-investment'
                    values={{
                        valueStart: toPrice(amountStart, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                        period: getPeriodText(period),
                        valuePeriod: toPrice(amountPeriod, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        })
                    }}
                >
                    ...
                    <span className="font-bold">valueStart</span>
                    <span className="font-bold">period</span>
                    <span className="font-bold">valuePeriod</span>
                    ...
                </Trans>
            </div>
            <div>
                <Button
                    className={`w-48 px-4 font-semibold`}
                    tint="green"
                    size="sm"
                    onClick={() => {} // TODO: implement functionality (ticket 2905, ROBO 419)
                }>
                    {t('general-open-account')}
                </Button>
            </div>  
        </div>
    );
};

const OtherIncomeText: React.FC<{ otherIncome: OtherIncome, amount: number }> = ({ otherIncome, amount }) => {
    const { t } = useTranslation('pension');
    
    return (
        <div>
            <Trans
                t={t}
                i18nKey={
                    otherIncome === 'gift'
                        ? 'pension-result-screen-summary-gift'
                        : otherIncome === 'heritage'
                            ? 'pension-result-screen-summary-heritage'
                            : otherIncome === 'surplusHouse'
                                ? 'pension-result-screen-summary-surplus'
                                : 'pension-result-screen-summary-other'}
                values={{
                    value: toPrice(amount, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })
                }}
            >
                ...
                <span className="font-bold">value</span>
                ...
            </Trans>
        </div>
    );
};

export const BNDList: React.FC<{} & HTMLProps<HTMLDivElement>> = ({ children }) => (
    <ul className='ml-6 list-disc'>
        {!!children && React.Children.map(children, child => {
            // Item has to contain some text, feel free to implement something more elegant.
            if ((child?.toString().length ?? 0) > 0) {
                return (
                    <li className='text-bnd-green mb-2'><div className='text-black inline'>{child}</div></li>
                );
            }
        })}
    </ul>
);

const getOverageOrShortage = (isGoalAchieved: boolean) => isGoalAchieved ? 'overschot' : 'tekort';

const getPensionAgeAltered = (pensionAgeDelta: number) => pensionAgeDelta < 0 ? 'verlaagd' : 'verhoogd';

const getPeriodText = (period: recurringPaymentInterval) => period === 'month' ? 'maandelijks' : 'jaarlijks';